import { useEffect, useRef, useState } from "react";
import { api, idr } from "../../lib/api";
import { toast } from "sonner";
import { PieChart, TrendingUp, AlertTriangle, Wallet, Percent, Package } from "lucide-react";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

// Awal bulan berjalan (WIB) — default periode laporan bulan ini, bukan "hari ini".
const firstDayOfMonth = () => `${bulanIniISO()}-01`;

export default function FinanceLabaKategori() {
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth());
  const [dateTo, setDateTo] = useState(hariIniISO());
  const [cabang, setCabang] = useState("");
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false); // backend belum ter-restart / belum tersedia
  const [errMsg, setErrMsg] = useState("");

  // Nomor urut request — filter yang diklik beruntun tidak boleh membiarkan
  // respons lama menimpa hasil filter aktif (pola yang sama dengan AttendanceReview.jsx).
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setNotFound(false);
    setErrMsg("");
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (cabang) p.set("cabang", cabang);
    api.get(`/finance/laporan/kategori?${p}`)
      .then(r => { if (seq === reqSeq.current) setData(r.data); })
      .catch(e => {
        if (seq !== reqSeq.current) return;
        if (e?.response?.status === 404) {
          setNotFound(true);
        } else {
          setErrMsg(e?.response?.data?.detail || "Gagal memuat laporan laba per kategori");
          toast.error(e?.response?.data?.detail || "Gagal memuat laporan laba per kategori");
        }
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dateFrom, dateTo, cabang]);

  // Backend (`hitung_laporan_kategori` di reports.py) mengembalikan kunci `per_kategori`.
  const items = data?.per_kategori || [];
  const total = data?.total || { omzet: 0, hpp: 0, laba: 0, qty: 0 };
  const totalLabaAbs = items.reduce((a, it) => a + Math.abs(it.laba || 0), 0);

  // Hasil AGREGASI backend dikelompokkan per kategori (daftar kategori kecil
  // & tetap) — bukan potongan N-teratas, aman disortir tanpa BatasDaftarNotice.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kategori: (it) => it.label || it.kategori || "",
    qty: (it) => it.qty || 0,
    omzet: (it) => it.omzet || 0,
    hpp: (it) => it.hpp || 0,
    laba: (it) => it.laba || 0,
    marjin: (it) => (it.omzet ? (it.laba / it.omzet) * 100 : 0),
  });

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="finance-laba-kategori">
      <header>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Laporan &amp; Kontrol</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Laba per Kategori</h1>
        <p className="text-sm text-slate-600 mt-1">
          Omzet, HPP, dan laba dipecah per kategori barang — Unit Baru, Unit Second, Aksesoris, Aktivasi IMEI, SIM Sticker.
        </p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="laba-kat-from"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="laba-kat-to"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Cabang</span>
          <select value={cabang} onChange={e => setCabang(e.target.value)}
            data-testid="laba-kat-cabang"
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Cabang"}</option>)}
          </select>
        </label>
      </div>

      {notFound && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-3" data-testid="laba-kat-notfound">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Laporan belum tersedia</p>
            <p className="text-xs text-amber-800 mt-1">
              Endpoint laporan laba per kategori belum aktif di server. Coba lagi setelah backend selesai deploy.
            </p>
          </div>
        </div>
      )}

      {!notFound && (
        <LoadingState loading={loading} error={errMsg || null} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-laba-kategori">
        <>
          {/* Ringkasan total */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={TrendingUp} label="Omzet" value={idr(total.omzet)} tint="slate" />
            <Kpi icon={Wallet} label="HPP" value={idr(total.hpp)} tint="amber" />
            <Kpi icon={Percent} label="Laba" value={idr(total.laba)} tint="emerald" />
            <Kpi icon={Package} label="Qty Terjual" value={total.qty ?? 0} tint="sky" />
          </div>

          {/* Bar proporsi laba per kategori — CSS murni */}
          {items.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="font-display font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <PieChart className="w-4 h-4 text-emerald-600" /> Proporsi Laba per Kategori
              </h3>
              <p className="text-xs text-slate-500 mb-4">Panjang batang = share laba kategori terhadap total laba absolut semua kategori.</p>
              <div className="space-y-3">
                {items.map(it => {
                  const share = totalLabaAbs > 0 ? Math.abs(it.laba || 0) / totalLabaAbs * 100 : 0;
                  const negatif = (it.laba || 0) < 0;
                  return (
                    <div key={it.kategori} data-testid={`laba-kat-bar-${it.kategori}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-medium text-slate-700">{it.label || it.kategori}</span>
                        <span className={`font-mono font-semibold ${negatif ? "text-rose-600" : "text-emerald-700"}`}>{idr(it.laba)}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${negatif ? "bg-rose-500" : "bg-emerald-600"}`}
                          style={{ width: `${Math.max(share, it.laba ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabel per kategori */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-slate-900">Rincian per Kategori</h3>
            </div>
            <table className="w-full text-sm" data-testid="laba-kat-tabel">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="laba-kat-sort-kategori" />
                  <SortTh label="Qty" sortKeyName="qty" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-qty" />
                  <SortTh label="Omzet" sortKeyName="omzet" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-omzet" />
                  <SortTh label="HPP" sortKeyName="hpp" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-hpp" />
                  <SortTh label="Laba" sortKeyName="laba" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-laba" />
                  <SortTh label="Marjin" sortKeyName="marjin" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="laba-kat-sort-marjin" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-sm text-slate-400" data-testid="laba-kat-empty">
                      Tidak ada data pada rentang &amp; cabang ini.
                    </td>
                  </tr>
                )}
                {sortedItems.map(it => {
                  const marjin = it.omzet ? (it.laba / it.omzet) * 100 : 0;
                  return (
                    <tr key={it.kategori} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`laba-kat-row-${it.kategori}`}>
                      <td className="py-3 px-4 font-medium text-slate-900">{it.label || it.kategori}</td>
                      <td className="py-3 px-4 text-right font-mono">{it.qty ?? 0}</td>
                      <td className="py-3 px-4 text-right font-mono">{idr(it.omzet)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{idr(it.hpp)}</td>
                      <td className={`py-3 px-4 text-right font-mono font-semibold ${(it.laba || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{idr(it.laba)}</td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600">{marjin.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                    <td className="py-3 px-4 text-slate-900">Total</td>
                    <td className="py-3 px-4 text-right font-mono">{total.qty ?? 0}</td>
                    <td className="py-3 px-4 text-right font-mono">{idr(total.omzet)}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-600">{idr(total.hpp)}</td>
                    <td className={`py-3 px-4 text-right font-mono ${(total.laba || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{idr(total.laba)}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-600">{total.omzet ? ((total.laba / total.omzet) * 100).toFixed(1) : "0.0"}%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
        </LoadingState>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className={`w-8 h-8 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
